import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    Injector,
    OnDestroy,
    OnInit,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { IFeedbackDialogResult } from '@fi-shared/fi-support/feedback';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { IFiUserData } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { FiContactSupportSectionBase } from '@fi-shared/fi-support/fi-contact-support-dialog/fi-contact-support-section-base';
import { ContactSupportOptionType } from '@fi-shared/fi-support/fi-contact-support-dialog/fi-contact-support-dialog.component';
import { NgModel } from '@angular/forms';
import { FiDndHelper } from '@fi-shared/fi-insert-media/fi-dnd-overlay/fi-dnd-helper';
import { takeUntil } from 'rxjs/operators';
import { FiFileUpload2Service } from '@fi-shared/fi-insert-media/fi-file-upload2.service';
import { FiDndOverlayComponent } from '@fi-shared/fi-insert-media/fi-dnd-overlay/fi-dnd-overlay.component';

@Component({
    selector: 'fi-contact-support-email-section',
    templateUrl: './fi-contact-support-email-section.component.html',
    styleUrls: ['./fi-contact-support-email-section.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-contact-support-email-section' },
    standalone: false,
})
export class FiContactSupportEmailSectionComponent extends FiContactSupportSectionBase implements OnInit {
    files$ = new BehaviorSubject<File[]>([]);
    contactSupportData: IFeedbackDialogResult;
    emailDescriptionMinLength: number;
    currentOptionType: ContactSupportOptionType;

    @ViewChild('fiDndOverlayComponent') fiDndOverlayComponent: FiDndOverlayComponent;
    @ViewChild('descriptionModel') descriptionModel: NgModel;

    private readonly emailDescriptionTechnicalMinLength: number = 50;
    private readonly emailDescriptionNonTechnicalMinLength: number = 20;

    constructor(
        injector: Injector,
        public elementRef: ElementRef,
    ) {
        super(injector);
    }

    ngOnInit(): void {
        this.setInitialSupportData();
        this.setDescriptionMinLength();
        this.supportService.getCurrentSupportOptionType();
    }

    onDescriptionChanged() {
        this.descriptionModel.control.markAsTouched();
    }

    getEmailDescriptionMinLength() {
        // https://chainformation.atlassian.net/browse/FI-4767
        // whitespaces and new lines should not count as character
        const descriptionWithoutWhitespaces = this.contactSupportData.contactSupport.Description.replace(/ /g, '')
            .replace(/\r/g, '')
            .replace(/\n/g, '');
        const whiteSpacesCount =
            this.contactSupportData.contactSupport.Description.length - descriptionWithoutWhitespaces.length;
        return this.emailDescriptionMinLength + whiteSpacesCount;
    }

    isDraggingFile(): Observable<boolean> {
        return this.fiDndOverlayComponent?.fiDndHelper.dragging$;
    }

    private setInitialSupportData() {
        const userData: IFiUserData = this.userDataService.getUserData();
        this.currentOptionType = this.supportService.getCurrentSupportOptionType();

        this.contactSupportData = {
            attachScreenshot: this.isTechnicalIssue(),
            files: [],
            contactSupport: {
                UserId: userData?.UserId,
                UserName: userData?.FullName,
                PhoneNumber: userData?.UserPhone,
                EmailAddress: userData?.UserEmail,
                Description: '',
                SupportType: this.currentOptionType,
            },
        };
    }

    private setDescriptionMinLength() {
        this.emailDescriptionMinLength = this.isTechnicalIssue()
            ? this.emailDescriptionTechnicalMinLength
            : this.emailDescriptionNonTechnicalMinLength;
    }

    private isTechnicalIssue(): boolean {
        return this.currentOptionType === ContactSupportOptionType.Technical;
    }

    getMinLengthErrorMessage(): string {
        return this.appTextService.get('error_message.min_length_error', {
            '{0}': this.emailDescriptionMinLength,
        });
    }

    onFilesSelect($event: ArrayLike<File>) {
        if ($event.length > 0) {
            const newFiles: File[] = this.files$.value;
            newFiles.push(...Array.from($event));
            this.files$.next(newFiles);
        }
    }

    sendMessage() {
        this.contactSupportData.files = this.files$.value;
        const promise = this.supportService.sendFeedback(this.contactSupportData);
        this.fiSpinnerService.trackPromise(promise, {
            headerText: this.appTextService.get('support.sending_message'),
            messageText: this.appTextService.get('support.sending_message_text'),
            isLoadingProgressHidden: true,
        });
        super.closeDialog();
    }

    getEmailSectionHeader(): string {
        if (this.currentOptionType === ContactSupportOptionType.Account)
            return this.appTextService.get('support.option_account_header');

        if (this.currentOptionType === ContactSupportOptionType.Content)
            return this.appTextService.get('support.option_content_header');

        if (this.currentOptionType === ContactSupportOptionType.Technical)
            return this.appTextService.get('support.option_technical_header');

        if (this.currentOptionType === ContactSupportOptionType.FeedbackChainformation)
            return this.appTextService.get('support.option_feedback_chainformation_header');

        if (this.currentOptionType === ContactSupportOptionType.FeedbackInternal)
            return this.appTextService.get('support.option_feedback_internal_header');

        return this.appTextService.get('support.option_email_header');
    }

    getEmailDescriptionHeader(): string {
        if (this.currentOptionType === ContactSupportOptionType.FeedbackChainformation)
            return this.appTextService.get('support.option_email_description_chainformation_feedback');

        if (this.currentOptionType === ContactSupportOptionType.FeedbackInternal)
            return this.appTextService.get('support.option_email_description_internal_feedback');

        return this.appTextService.get('support.email_section_description');
    }
}
